import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { capitalizeFirstLetter } from "../../../../components/utils/Date";
import { getCategoryIconComponent } from "../wasteCategoryIcons";
import {
  applySortingItemsToFormData,
  countSortingMaterialsByStream,
  matchesSortingStream,
} from "../wasteSortingItems";
import {
  compareParentCategories,
  compareSortingMaterials,
} from "../wasteMaterialSort";

const WasteFlowSortingTab = ({
  formData,
  setFormData,
  items,
  setItems,
  streamType,
  catalogReady = false,
}) => {
  const { t, i18n } = useTranslation();

  const collator = useMemo(
    () =>
      new Intl.Collator(i18n.language, {
        sensitivity: "base",
        numeric: true,
      }),
    [i18n.language],
  );

  const filteredItems = useMemo(
    () => items.filter((m) => matchesSortingStream(m, streamType)),
    [items, streamType],
  );

  const streamMaterialCount = useMemo(
    () => countSortingMaterialsByStream(items, streamType),
    [items, streamType],
  );

  const formatCategoryLabel = useCallback(
    (categoryKey) => {
      if (!categoryKey || typeof categoryKey !== "string") return "—";
      const pluralKey = `${categoryKey}_plural`;
      const plural = t(pluralKey);
      if (plural && plural !== pluralKey) {
        return capitalizeFirstLetter(plural);
      }
      const translated = t(categoryKey);
      if (!translated || typeof translated !== "string") return categoryKey;
      return capitalizeFirstLetter(translated);
    },
    [t],
  );

  const categories = useMemo(() => {
    const keys = [
      ...new Set(filteredItems.map((v) => v.parentCategory).filter(Boolean)),
    ];
    return keys.sort((a, b) =>
      compareParentCategories(a, b, formatCategoryLabel, collator),
    );
  }, [filteredItems, collator, formatCategoryLabel]);

  const getCategoryItems = (category) =>
    filteredItems
      .filter(
        (item) =>
          item.parentCategory === category &&
          item.isActiveSortingError === true,
      )
      .sort((a, b) =>
        compareSortingMaterials(
          a,
          b,
          (material) => t(material.referenceName),
          collator,
        ),
      );

  const isCategoryActive = (category) => {
    const categoryItems = getCategoryItems(category);
    return categoryItems.some((item) => item.isChecked === true);
  };

  const pushFormUpdate = (updatedItems) => {
    setItems(updatedItems);
    setFormData(applySortingItemsToFormData(formData, updatedItems, t));
  };

  const handleCategoryToggle = (category) => {
    const active = isCategoryActive(category);
    const updatedItems = items.map((item) =>
      item.parentCategory === category && matchesSortingStream(item, streamType)
        ? { ...item, isChecked: !active }
        : item,
    );
    pushFormUpdate(updatedItems);
  };

  const handleMaterialToggle = (itemId) => {
    const updatedItems = items.map((item) =>
      item._id === itemId ? { ...item, isChecked: !item.isChecked } : item,
    );
    pushFormUpdate(updatedItems);
  };

  const emptyMessage = (() => {
    if (!catalogReady) return t("settingsWaste.sortingCatalogLoading");
    if (items.length === 0) return t("settingsWaste.sortingNoMaterials");
    if (streamMaterialCount === 0) {
      return streamType === "hw"
        ? t("settingsWaste.sortingNoHwMaterials")
        : t("settingsWaste.sortingNoProMaterials");
    }
    return null;
  })();

  return (
    <div className="waste-flow-sorting-panel">
      {emptyMessage && (
        <p className="waste-flow-sorting-panel__empty">{emptyMessage}</p>
      )}
      {categories.map((category) => {
        const CategoryIcon = getCategoryIconComponent(category);
        const categoryItems = getCategoryItems(category);
        const categoryOn = isCategoryActive(category);

        return (
          <div
            key={`sorting-cat-${category}`}
            className={`waste-flow-sorting-card${categoryOn ? " waste-flow-sorting-card--expanded" : ""}`}
          >
            <div className="waste-flow-sorting-card__header">
              <div className="waste-flow-sorting-card__icon" aria-hidden>
                <CategoryIcon />
              </div>
              <span className="waste-flow-sorting-card__title">
                {formatCategoryLabel(category)}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={categoryOn}
                aria-label={formatCategoryLabel(category)}
                className={`waste-flow-toggle${categoryOn ? " waste-flow-toggle--on" : ""}`}
                onClick={() => handleCategoryToggle(category)}
              >
                <span className="waste-flow-toggle__thumb" />
              </button>
            </div>

            {categoryOn && categoryItems.length > 0 && (
              <div className="waste-flow-sorting-card__materials">
                {categoryItems.map((item) => (
                  <label
                    key={`sorting-item-${item._id}`}
                    className="waste-flow-sorting-checkbox"
                  >
                    <input
                      type="checkbox"
                      className="waste-flow-sorting-checkbox__input"
                      checked={Boolean(item.isChecked)}
                      onChange={() => handleMaterialToggle(item._id)}
                    />
                    <span className="waste-flow-sorting-checkbox__box" />
                    <span className="waste-flow-sorting-checkbox__label">
                      {t(item.referenceName)}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default WasteFlowSortingTab;
